/**
 * Pill-style reason chip used in the unified `DataViewFilterPanel`'s
 * Reason section. Replaces the nested teal sub-card box that used to
 * render below the active parent tile.
 *
 * Selected state mirrors the issue-area tile's teal accent so the path
 * (parent tile -> reason chip) reads as one selection rather than two
 * unrelated controls.
 */
import React from 'react'
import { Box, Chip } from '@mui/material'
import {
  ISSUE_CARD_ACTIVE_BG,
  ISSUE_CARD_ACTIVE_BORDER,
} from './IssueCard'
import { poolwerxColors } from '../../../theme/colors'

interface ReasonChipProps {
  label: string
  count: number | null | undefined
  active: boolean
  onClick: () => void
}

const ReasonChip: React.FC<ReasonChipProps> = ({
  label,
  count,
  active,
  onClick,
}) => {
  const isZero = count === 0
  return (
    <Chip
      size="small"
      clickable
      onClick={onClick}
      variant="outlined"
      label={
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
          <Box component="span">{label}</Box>
          {count != null ? (
            <Box
              component="span"
              sx={{
                color: active ? poolwerxColors.teal : 'text.disabled',
                fontWeight: 400,
                opacity: isZero && !active ? 0.6 : 1,
              }}
            >
              ({count.toLocaleString()})
            </Box>
          ) : null}
        </Box>
      }
      sx={{
        height: 28,
        borderRadius: 1.5,
        fontWeight: active ? 600 : 500,
        color: active ? poolwerxColors.teal : 'text.primary',
        borderColor: active ? ISSUE_CARD_ACTIVE_BORDER : 'rgba(0, 0, 0, 0.18)',
        backgroundColor: active ? ISSUE_CARD_ACTIVE_BG : 'transparent',
        opacity: isZero && !active ? 0.7 : 1,
        '& .MuiChip-label': { px: 1.25 },
        '&:hover': {
          backgroundColor: active ? ISSUE_CARD_ACTIVE_BG : '#EEF1F4',
          borderColor: active ? ISSUE_CARD_ACTIVE_BORDER : 'rgba(0, 0, 0, 0.3)',
        },
      }}
    />
  )
}

export default ReasonChip
